import type { UpdateReviewCycleRequest } from "@performyard/service-contracts";
import { convertToZonedUTCTime } from "@performyard/utils-isomorphic";

import { normalizeAutomationEndDate } from "../../utils/automation-end-date";

import type { CycleType, EditCycleFormData } from "./schema";

// Compares two dates by their absolute instant; treats null/undefined as "no date".
const datesDiffer = (a: Date | null | undefined, b: Date | null | undefined): boolean => {
	const aTime = a ? a.getTime() : null;
	const bTime = b ? b.getTime() : null;
	return aTime !== bTime;
};

// The effective automation end date for a form state, as a UTC instant: only rolling cycles
// carry an end date, always pinned to 00:30 company-local on the selected day.
const effectiveEndDate = (
	form: Pick<EditCycleFormData, "automationType" | "automationEndDate">,
	companyTimezone: string
): Date | null =>
	form.automationType === "recurring" && form.automationEndDate
		? normalizeAutomationEndDate(form.automationEndDate, companyTimezone)
		: null;

/**
 * Maps the Edit Cycle Details form values to the PUT /review-cycles/:id request body,
 * including ONLY the fields that actually changed from their prefilled values.
 *
 * Timezone: the date pickers hold company-timezone wall-clock times, so changed dates are
 * converted back to absolute UTC instants before being sent.
 */
export const buildUpdateCyclePayload = ({
	cycleType,
	formData,
	original,
	companyTimezone,
}: {
	cycleType: CycleType;
	formData: EditCycleFormData;
	original: EditCycleFormData;
	companyTimezone: string;
}): UpdateReviewCycleRequest => {
	const payload: UpdateReviewCycleRequest = {};

	const name = formData.name.trim();
	if (name !== original.name.trim()) {
		payload.name = name;
	}

	if (formData.lockReviews !== original.lockReviews) {
		payload.adminsOnly = formData.lockReviews;
	}

	if (formData.startDate && datesDiffer(formData.startDate, original.startDate)) {
		// The date picker yields a Date with wall-clock time as picked by the user,
		// but this is intended to be relative to the company timezone.
		// If we convert to UTC now, it will represent the wall-clock of the acting user's browser TZ,
		// instead, we need to explicitly convert the Date to the company timezone.
		payload.startDate = convertToZonedUTCTime(formData.startDate, companyTimezone);
	}

	if (cycleType === "automated") {
		const autoflow: NonNullable<UpdateReviewCycleRequest["autoflow"]> = {};

		if (formData.automationType !== original.automationType) {
			autoflow.type = formData.automationType === "recurring" ? "rolling" : "one-time";
		}

		const desiredEndDate = effectiveEndDate(formData, companyTimezone);
		if (datesDiffer(desiredEndDate, effectiveEndDate(original, companyTimezone))) {
			autoflow.endDate = desiredEndDate;
		}

		if (Object.keys(autoflow).length > 0) {
			payload.autoflow = autoflow;
		}
	}

	return payload;
};
